import { useMemo } from 'react'

type BreakpointConfig = number | { minWidth?: number; maxWidth?: number }

const isActive = (width: number, config: BreakpointConfig) => {
  if (typeof config === 'number') return width <= config

  const aboveMin = config.minWidth === undefined || width >= config.minWidth
  const belowMax = config.maxWidth === undefined || width <= config.maxWidth
  return aboveMin && belowMax
}

/**
 * Composable hook that derives named boolean breakpoints from a measured width.
 *
 * Use alongside `useResizeObserver` to implement JavaScript-driven container
 * queries or respond to container size changes for conditional rendering,
 * layout switching, or any logic that depends on an element's width.
 *
 * Each breakpoint key maps to either:
 * - A `number` (shorthand for `{ maxWidth: n }`) — active when `width <= n`
 * - An object with optional `minWidth` and/or `maxWidth` — active when width is within the range
 *
 * All breakpoints return `false` when `width` is `undefined` (not yet measured),
 * preventing layout flashes before the first ResizeObserver callback.
 */
export function useContainerBreakpoints<B extends Record<string, BreakpointConfig>>(
  width: number | undefined,
  breakpoints: B,
): { [_K in keyof B]: boolean } {
  return useMemo(() => {
    const result = {} as { [_K in keyof B]: boolean }

    for (const key of Object.keys(breakpoints) as Array<keyof B>) {
      result[key] = width !== undefined && isActive(width, breakpoints[key])
    }

    return result
  }, [width, breakpoints])
}
